import { Header } from '../common/Header';
import Card from '@mui/material/Card';
import TableGrid from '../table-grid/TableGrid';
import { EndPoints } from '../../axios/endPoints';
import { GridColDef } from '@mui/x-data-grid';
import { Button, Grid, IconButton, Tooltip } from '@mui/material';
import { RootState, useAppDispatch, useAppSelector } from '../../store/store';
import ImportTesterTrainingData from './ImportTesterTrainingData';
import { handleRefreshMLTesterTable, handleTesterRowClick, handleToggleDeleteFilePopup, handleToggleFileDetailsPopup, handleToggleImportTrainingDataDialog } from '../../store/mltester-slice/mltester';
import moment from 'moment';
import CloudDownloadTwoToneIcon from '@mui/icons-material/CloudDownloadTwoTone';
import DeleteForeverTwoToneIcon from '@mui/icons-material/DeleteForeverTwoTone';
import { DownloadFile } from '../../services/FileServices';
import { useTranslation } from 'react-i18next';
import InfoTwoToneIcon from '@mui/icons-material/InfoTwoTone';
import React, { useEffect, useMemo, useRef } from 'react';
import DeleteFilePopup from './DeleteFilePopup';
import CreatorFileDetails from '../ml-creator/CreatorFileDetails';
import TesterFileDetails from './TesterFileDetails';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import RedirectIconButton from '../common/RedirectIconButton';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import RefreshIcon from '@mui/icons-material/Refresh';
import Old_SelectedMLTester from './old_SelectedMLTester';

const Old_MLTester = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const selectedTesterRef = useRef<HTMLDivElement>();
    const memoizedTesterRef = useMemo(() => selectedTesterRef, [selectedTesterRef]);
    const { refreshMLTesterTable, selectedTesterRow } = useAppSelector((state: RootState) => state.mltester);

    const columns : GridColDef[] = useMemo(() => [
        { field: 'id', headerName: t('TESTER_ID_FIELD') },
        { field: 'name', headerName: t('TESTER_NAME_FIELD'), minWidth: 300,
            renderCell: (params) => {
                return(<div className="styled-div">{params.value}</div>)
            } 
        },
        { field: 'fileName', headerName: t('TESTER_FILE_NAME_FIELD'), minWidth: 300 },
        { field: 'rowCount', headerName: t('TESTER_ROW_COUNT_FIELD'), minWidth: 150 },
        { field: 'columnCount', headerName: t('TESTER_COLUMN_COUNT_FIELD'), minWidth: 150 },
        { field: 'uploadDate', headerName: t('TESTER_UPLOAD_DATE_FIELD'), renderCell: (params) => {
            return moment.utc(params.value).local().format('DD/MM/YYYY HH:mm')
        }, minWidth: 250 },
        { field: 'actions', headerName: t('TESTER_ACTIONS_FIELD'), minWidth: 300, renderCell: (params) => {
            return(
                <Grid container spacing={3}>
                    <Grid item>
                        <Tooltip title={t('TESTER_DETAILS_ACTION')}>
                            <IconButton onClick={() => {
                                dispatch(handleToggleFileDetailsPopup({
                                    open: true,
                                    body: {
                                        fileId: params.row.id,
                                        fileName: params.row.fileName
                                    }
                                }))
                            }}>
                                <InfoTwoToneIcon className="info-icon" />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                    <Grid item>
                        <Tooltip title={t('TESTER_DOWNLOAD_ACTION')}>
                            <IconButton onClick={() => {
                                DownloadFile(EndPoints.ML_TESTER_DOWNLOAD_TESTING_DATASET + `?id=${params.row.id}`)
                            }}>
                                <CloudDownloadTwoToneIcon className="download-icon" />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                    <Grid item>
                        <Tooltip title={t('TESTER_NEW_PREDICTION_ACTION')}>
                            <IconButton onClick={async (e) => { 
                                await dispatch(handleTesterRowClick(params.row));
                                if(selectedTesterRef && selectedTesterRef.current) {
                                    selectedTesterRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                }
                            }}>
                                <PlayCircleIcon className="play-icon" />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                    <Grid item>
                        <Tooltip title={t('TESTER_DELETE_ACTION')}>
                            <IconButton onClick={() => {
                                dispatch(handleToggleDeleteFilePopup({ open: true, body: {
                                    fileName: params.row.fileName,
                                    fileId: params.row.id
                                }}))
                            }}>
                                <DeleteForeverTwoToneIcon className="delete-icon" />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                </Grid>
            )
        }}
    ], [dispatch, t, selectedTesterRef])

    const handleToggleImportTrainingData = (isOpen: boolean) => {
        dispatch(handleToggleImportTrainingDataDialog(isOpen));
    }
    
    useEffect(() => {
        return () => dispatch(handleTesterRowClick(null));
    }, [])
    
    return(
        <React.Fragment>
            <Card style={{marginTop: '5px'}}>
                <Header 
                    title={t('SIDEBAR_ML_TESTER_TEXT')}
                    helperIcon={
                        <RedirectIconButton to={'/help/6'} className="helper-icon" >
                            <ContactSupportIcon />
                        </RedirectIconButton>
                    }
                />
                <Grid container p={2} spacing={2} sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Grid item display="flex" justifyContent="space-between">
                        <Button 
                            onClick={() => { handleToggleImportTrainingData(true)}} 
                            variant='contained' 
                            className='button-color'
                            size="small"
                        >
                            {t('CREATOR_IMPORT_TESTING_DATA_BUTTON')}
                        </Button>
                        <Tooltip title={t('REFRESH_TABLE_BUTTON_TOOLTIP')}>
                            <IconButton onClick={() => dispatch(handleRefreshMLTesterTable())}>
                                <RefreshIcon />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                    <Grid item sx={{ width: '100%', overflowY: 'auto' }}>
                        <TableGrid 
                            baseURL={EndPoints.ML_TESTER_GET_TESTING_DATASETS}
                            columns={columns}
                            sortColumns={[{ field: 'id', sort: 'asc' }]}
                            refresh={refreshMLTesterTable}
                            disableRowSelection
                            tableHeight={500}
                            onCellClick={async (params : any) => {
                                if(params.field == 'name') { 
                                    await dispatch(handleTesterRowClick(params.row));
                                    if(selectedTesterRef && selectedTesterRef.current) {
                                        selectedTesterRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                    }
                                }
                            }}
                            getCellClassName={(params: any) => {
                                if(params.field == 'name') return 'custom-pointer-cell';
                            }}
                            rowHeight={35}
                            columnHeaderHeight={40}
                            getRowClassName={(params: any) =>
                                params.id === selectedTesterRow?.id ? 'highlighted-row' : ''
                            }
                        />
                    </Grid>
                </Grid>
                <ImportTesterTrainingData />
                <DeleteFilePopup />
                <CreatorFileDetails />
                <TesterFileDetails />
            </Card>
            <br />
            <Old_SelectedMLTester ref={memoizedTesterRef as React.RefObject<HTMLDivElement>}/>
        </React.Fragment>
    )
}

export default Old_MLTester;